import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ShoppingBag, Hash, Heart, Bell, Loader2, Check } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { useGet } from "@/lib/hooks";
import { useCart } from "@/context/CartContext";
import { ProtectedImage } from "@/components/wp/ProtectedImage";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { Reveal } from "@/components/wp/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

function NotifyInline({ initiative }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!email || !email.includes("@")) return toast.error("Please enter a valid email.");
    setLoading(true);
    try { await api.post("/notify", { email, initiative }); setDone(true); toast.success("You're on the waitlist \uD83D\uDC9D"); }
    catch { toast.error("Could not join waitlist."); }
    finally { setLoading(false); }
  };
  if (done) return <p className="flex items-center gap-1.5 text-sm text-wp-success"><Check className="h-4 w-4" /> We'll notify you!</p>;
  return (
    <div className="flex gap-2">
      <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for the waitlist" className="h-10 rounded-xl" data-testid="founder-notify-email" />
      <Button onClick={submit} disabled={loading} className="h-10 rounded-xl bg-wp-rose-strong text-white" data-testid="founder-notify-button">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
      </Button>
    </div>
  );
}

export default function Founder() {
  const { data: items } = useGet("/founder");
  const { addItem } = useCart();
  const list = items || [];

  return (
    <div>
      {/* storytelling hero */}
      <section className="wp-hero-gradient wp-noise relative overflow-hidden">
        <div className="wp-container relative py-14 text-center sm:py-20">
          <Reveal>
            <span className="wp-chip"><Sparkles className="h-3.5 w-3.5 text-wp-rose-strong" /> Limited · Handmade · Hand-numbered</span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl">The Founder Collection</h1>
            <p className="mx-auto mt-4 max-w-xl text-wp-ink-muted">
              A handful of one-of-a-kind pieces, hand-painted by our founder. Never mass produced — each is numbered and made with love. When they're gone, they're gone (until the next batch).
            </p>
          </Reveal>
        </div>
      </section>

      <section className="wp-section">
        <div className="wp-container">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((p, i) => {
              const soldOut = p.is_sold_out;
              return (
                <Reveal key={p.id} delay={(i % 3) * 0.06}>
                  <div className="group overflow-hidden rounded-wp-lg border border-border bg-card shadow-wp-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-wp-lift" data-testid={`founder-card-${p.id}`}>
                    <div className="relative aspect-square overflow-hidden bg-wp-cream-100">
                      <ProtectedImage src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <Badge className="absolute left-3 top-3 border-0 bg-wp-rose-strong text-white"><Hash className="mr-1 h-3 w-3" /> {p.limited?.batch_label || "Limited"}</Badge>
                      {soldOut && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/50 text-white backdrop-blur-[2px]">
                          <span className="wp-label text-sm">Sold Out</span>
                          <span className="text-xs">{p.limited?.next_batch_message || "Next Batch Coming Soon"}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-display text-lg leading-tight">{p.name}</h3>
                      <p className="mt-1 line-clamp-2 text-sm text-wp-ink-muted">{p.description}</p>
                      <div className="mt-3 flex items-center gap-2 text-xs text-wp-ink-muted">
                        {p.limited?.hand_numbered && <span className="wp-chip"><Hash className="h-3 w-3" /> Hand-numbered</span>}
                        <span className="wp-chip"><Heart className="h-3 w-3 text-wp-rose-strong" /> Made with love</span>
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="price-num text-lg font-semibold text-wp-rose-strong">{formatINR(p.price)}</span>
                        {!soldOut && (
                          <span className="text-xs font-medium text-wp-ink-muted">Only {p.inventory} left</span>
                        )}
                      </div>
                      {soldOut ? (
                        <div className="mt-3"><NotifyInline initiative={`founder:${p.id}`} /></div>
                      ) : (
                        <Button onClick={() => { addItem({ id: p.id, name: p.name, price: p.price, image: p.image, type: "product" }); toast.success(`${p.name} added \uD83D\uDC9D`); }}
                          data-testid={`founder-add-${p.id}`} className="mt-3 w-full rounded-xl bg-wp-rose-strong text-white hover:brightness-105">
                          <ShoppingBag className="mr-2 h-4 w-4" /> Add to Cart
                        </Button>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-10 text-center text-sm text-wp-ink-muted">Every purchase supports handmade craft. 🩷 <Link to="/shop" className="text-wp-rose-strong hover:underline">Browse gift parcels</Link></p>
        </div>
      </section>
    </div>
  );
}
